import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Loader2, FileText, Building2, Mail, Phone, User, IndianRupee, Calendar,
  CheckCircle2, Clock, XCircle, ExternalLink, Receipt, AlertCircle, Search, ShieldCheck, Wrench, RefreshCw, FileEdit,
} from 'lucide-react';
import { toast } from 'sonner';
import KYCReviewModal from './KYCReviewModal';

const STATUS_STYLES = {
  pending_payment: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'Pending Payment', icon: Clock },
  active: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Active', icon: CheckCircle2 },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Cancelled', icon: XCircle },
  expired: { bg: 'bg-slate-500/10', text: 'text-slate-500', label: 'Expired', icon: XCircle },
  payment_failed: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Payment Failed', icon: AlertCircle },
};

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const CancelDialog = ({ open, onClose, sub, onConfirm, submitting }) => {
  const [reason, setReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(0);
  useEffect(() => { if (open) { setReason(''); setRefundAmount(0); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="vo-cancel-dialog">
        <DialogHeader><DialogTitle>Cancel Virtual Office Subscription</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-2.5 rounded bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-sm">
            <p className="font-semibold">{sub?.company_name}</p>
            <p className="text-xs text-muted-foreground">{sub?.tier_name} · {sub?.venue_name}</p>
            <p className="text-xs mt-1">Total paid: <strong>{fmtINR(sub?.total_amount)}</strong></p>
          </div>
          <div>
            <Label>Cancellation Reason *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Explain the reason for cancellation..." data-testid="vo-cancel-reason" />
          </div>
          <div>
            <Label>Refund Amount (₹)</Label>
            <Input type="number" min="0" max={sub?.total_amount} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="vo-cancel-refund" />
            <p className="text-xs text-muted-foreground mt-1">Enter 0 for no refund. Refund will be processed via Razorpay to original payment method.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="destructive" onClick={() => onConfirm({ reason, refund_amount: parseFloat(refundAmount) || 0 })} disabled={submitting || reason.length < 3} data-testid="vo-cancel-confirm">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Cancel Subscription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const VirtualOfficeSubscriptionsTab = ({ api, theme = {}, isAdmin = false }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [cancelSub, setCancelSub] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [kycSub, setKycSub] = useState(null);
  const [regenSub, setRegenSub] = useState(null);
  const [fixingDrafts, setFixingDrafts] = useState(false);
  const [recordingPaid, setRecordingPaid] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const reconcilePendingPayments = async () => {
    if (!window.confirm('This will check Razorpay for every Pending Payment subscription and activate the ones that have actually been paid. Continue?')) return;
    setReconciling(true);
    try {
      const res = await api.post('/virtual-office/admin/reconcile-pending-vo-payments');
      const { checked, activated, still_unpaid, errors } = res.data;
      const msg = `Checked ${checked} · Activated ${activated} · Still unpaid ${still_unpaid}${errors?.length ? ` · ${errors.length} errors` : ''}`;
      if (activated > 0) toast.success(msg);
      else toast.info(msg);
      if (errors?.length) console.warn('[VO-RECONCILE] errors:', errors);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  const fixDraftInvoices = async () => {
    if (!window.confirm('This will approve + mark-as-sent ALL Zoho invoices currently in draft status. Continue?')) return;
    setFixingDrafts(true);
    try {
      const res = await api.post('/virtual-office/admin/mark-invoices-sent');
      const { total, fixed, failed } = res.data;
      if (failed > 0) {
        toast.warning(`Fixed ${fixed}/${total}. ${failed} invoice(s) couldn't be fixed — see console.`);
        console.warn('Backfill failures:', res.data.failed_details);
      } else {
        toast.success(`All ${fixed} draft invoice(s) are now Sent — links will work.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setFixingDrafts(false);
    }
  };

  const recordPaidInvoices = async () => {
    if (!window.confirm('This will record the Razorpay payment in Zoho for every active VO subscription + paid booking, moving "Outstanding" invoices to "Paid". Continue?')) return;
    setRecordingPaid(true);
    try {
      const res = await api.post('/virtual-office/admin/record-paid-invoices');
      const { vo, bookings } = res.data;
      const msg = `VO: ${vo.recorded}/${vo.total} recorded (${vo.skipped} already done, ${vo.failed} failed) · Bookings: ${bookings.recorded}/${bookings.total} recorded (${bookings.skipped} already done, ${bookings.failed} failed).`;
      if ((vo.failed + bookings.failed) > 0) toast.warning(msg);
      else toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setRecordingPaid(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/virtual-office/subscriptions${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`);
      setSubs(res.data || []);
    } catch (e) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = subs.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.company_name || '').toLowerCase().includes(q) ||
      (s.contact_name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.phone || '').includes(q) ||
      (s.venue_name || '').toLowerCase().includes(q)
    );
  });

  const handleCancel = async ({ reason, refund_amount }) => {
    if (!cancelSub) return;
    setCancelling(true);
    try {
      await api.patch(`/virtual-office/subscriptions/${cancelSub.id}/cancel`, { reason, refund_amount });
      toast.success('Subscription cancelled');
      setCancelSub(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancellation failed');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  // Summary stats
  const stats = subs.reduce((acc, s) => {
    acc.total++;
    acc[s.status] = (acc[s.status] || 0) + 1;
    if (s.status === 'active') acc.revenue += s.total_amount || 0;
    return acc;
  }, { total: 0, revenue: 0 });

  return (
    <div className="space-y-4" data-testid="vo-subscriptions-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={theme.cardBg}><CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Subscriptions</p>
          <p className="text-2xl font-bold mt-1">{stats.total}</p>
        </CardContent></Card>
        <Card className={theme.cardBg}><CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Active</p>
          <p className="text-2xl font-bold mt-1 text-green-600">{stats.active || 0}</p>
        </CardContent></Card>
        <Card className={theme.cardBg}><CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Pending Payment</p>
          <p className="text-2xl font-bold mt-1 text-amber-600">{stats.pending_payment || 0}</p>
        </CardContent></Card>
        <Card className={theme.cardBg}><CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Annual Revenue</p>
          <p className="text-2xl font-bold mt-1">{fmtINR(stats.revenue)}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by company / contact / email / venue..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`pl-8 ${theme.inputBg}`}
            data-testid="vo-subs-search"
          />
        </div>
        {['all', 'active', 'pending_payment', 'cancelled', 'expired'].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? 'default' : 'outline'}
            onClick={() => setStatusFilter(s)}
            data-testid={`vo-subs-filter-${s}`}
          >
            {s === 'all' ? 'All' : STATUS_STYLES[s]?.label || s}
          </Button>
        ))}
        {isAdmin && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={reconcilePendingPayments}
              disabled={reconciling}
              className="ml-auto border-blue-500/50 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
              data-testid="reconcile-pending-vo-btn"
              title="Poll Razorpay for every 'Pending Payment' subscription and activate the ones already paid. Fixes subs stuck in pending if the webhook was missed."
            >
              {reconciling ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Reconcile Pending Payments
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={fixDraftInvoices}
              disabled={fixingDrafts}
              className="border-amber-500/50 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
              data-testid="fix-draft-invoices-btn"
              title="One-click: approve & mark-sent every Zoho invoice currently stuck in draft so the public links work."
            >
              {fixingDrafts ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wrench className="w-4 h-4 mr-1.5" />}
              Fix Draft Invoices
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={recordPaidInvoices}
              disabled={recordingPaid}
              className="border-emerald-500/50 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              data-testid="record-paid-invoices-btn"
              title="One-click: record the Razorpay payment in Zoho for every paid VO sub + booking so Outstanding invoices flip to Paid."
            >
              {recordingPaid ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Mark Paid in Zoho
            </Button>
          </>
        )}
      </div>

      {filtered.length === 0 && (
        <Card className={theme.cardBg}>
          <CardContent className="p-12 text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-40" />
            <p>No subscriptions yet.</p>
          </CardContent>
        </Card>
      )}

      {filtered.map((s) => {
        const st = STATUS_STYLES[s.status] || STATUS_STYLES.pending_payment;
        const Icon = st.icon;
        return (
          <Card key={s.id} className={theme.cardBg} data-testid={`vo-sub-${s.id}`}>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-lg">{s.company_name}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${st.bg} ${st.text} flex items-center gap-1 font-medium`}>
                      <Icon className="w-3 h-3" /> {st.label}
                    </span>
                    <Badge variant="outline" className="text-xs">{s.tier_name}</Badge>
                    {s.source === 'sales_link' && <Badge variant="outline" className="text-xs">Sales</Badge>}
                    {s.kyc_status && s.kyc_status !== 'pending' && (
                      <Badge variant="outline" className={`text-xs ${
                        s.kyc_status === 'approved' ? 'border-green-500 text-green-600' :
                        s.kyc_status === 'rejected' ? 'border-red-500 text-red-600' :
                        s.kyc_status === 'submitted' ? 'border-amber-500 text-amber-600' :
                        'border-blue-500 text-blue-600'
                      }`}>
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        KYC: {s.kyc_status.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Building2 className="w-3 h-3" /> {s.venue_name} · Created {fmtDate(s.created_at)}
                  </p>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {s.razorpay_payment_link_url && s.status === 'pending_payment' && (
                    <Button size="sm" variant="outline" onClick={() => window.open(s.razorpay_payment_link_url, '_blank')} data-testid={`vo-sub-link-${s.id}`}>
                      <ExternalLink className="w-3 h-3 mr-1" /> Payment Link
                    </Button>
                  )}
                  {s.zoho_invoice_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await api.get(`/virtual-office/subscriptions/${s.id}/invoice.pdf`, { responseType: 'blob' });
                          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 30000);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Could not load invoice. Please try again.');
                        }
                      }}
                      data-testid={`vo-sub-invoice-${s.id}`}
                    >
                      <Receipt className="w-3 h-3 mr-1" /> Invoice
                    </Button>
                  )}
                  {s.kyc_status === 'submitted' && (
                    <Button size="sm" variant="outline" onClick={() => setKycSub(s)} className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid={`vo-sub-kyc-${s.id}`}>
                      <ShieldCheck className="w-3 h-3 mr-1" /> Review KYC
                    </Button>
                  )}
                  {isAdmin && s.zoho_invoice_id && (
                    <Button size="sm" variant="outline" onClick={() => setRegenSub(s)} className="border-blue-500/60 text-blue-700 hover:bg-blue-50" data-testid={`vo-sub-regen-${s.id}`}>
                      <FileEdit className="w-3 h-3 mr-1" /> Fix Invoice
                    </Button>
                  )}
                  {isAdmin && (s.status === 'active' || s.status === 'pending_payment') && (
                    <Button size="sm" variant="destructive" onClick={() => setCancelSub(s)} data-testid={`vo-sub-cancel-${s.id}`}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-muted-foreground" /> {s.contact_name}</div>
                <a href={`mailto:${s.email}`} className="flex items-center gap-1.5 hover:text-primary truncate"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> {s.email}</a>
                <a href={`tel:${s.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {s.phone}</a>
                <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-muted-foreground" /> {fmtDate(s.start_date)} → {fmtDate(s.end_date)}</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3 p-2.5 rounded bg-muted/30">
                <div><p className="text-xs text-muted-foreground">Annual Base</p><p className="font-medium">{fmtINR(s.base_amount)}</p></div>
                <div><p className="text-xs text-muted-foreground">GST (18%)</p><p className="font-medium">{fmtINR(s.gst_amount)}</p></div>
                <div><p className="text-xs text-muted-foreground">Total</p><p className="font-medium text-primary">{fmtINR(s.total_amount)}</p></div>
                {s.gstin && <div><p className="text-xs text-muted-foreground">GSTIN</p><p className="font-mono text-xs">{s.gstin}</p></div>}
              </div>
              {s.cancellation_reason && (
                <div className="mt-3 p-2.5 rounded bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm">
                  <p className="text-xs font-medium text-red-700 dark:text-red-400">Cancellation Reason</p>
                  <p className="text-sm mt-1">{s.cancellation_reason}</p>
                  {s.refund_amount > 0 && <p className="text-xs text-muted-foreground mt-1">Refund: {fmtINR(s.refund_amount)}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <CancelDialog
        open={!!cancelSub}
        onClose={() => setCancelSub(null)}
        sub={cancelSub}
        onConfirm={handleCancel}
        submitting={cancelling}
      />
      <KYCReviewModal
        open={!!kycSub}
        onClose={() => setKycSub(null)}
        sub={kycSub}
        api={api}
        onReviewed={load}
      />
      <RegenerateInvoiceDialog
        open={!!regenSub}
        onClose={() => setRegenSub(null)}
        sub={regenSub}
        api={api}
        onDone={load}
      />
    </div>
  );
};

export default VirtualOfficeSubscriptionsTab;

/* ============================================================
 * Regenerate-Invoice modal — admin enters corrected company + GSTIN.
 * Hits POST /virtual-office/admin/regenerate-invoice/{sub_id}
 * ============================================================ */
const RegenerateInvoiceDialog = ({ open, onClose, sub, api, onDone }) => {
  const [companyName, setCompanyName] = useState('');
  const [gstin, setGstin] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && sub) {
      setCompanyName(sub.company_name || '');
      setGstin(sub.gstin || '');
      setCompanyAddress(sub.company_address || '');
    }
  }, [open, sub]);

  const submit = async () => {
    if (!companyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (gstin && gstin.trim().length !== 15) {
      toast.error('GSTIN must be exactly 15 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/virtual-office/admin/regenerate-invoice/${sub.id}`, {
        company_name: companyName.trim(),
        gstin: gstin.trim() || null,
        company_address: companyAddress.trim() || null,
      });
      toast.success(`Invoice regenerated: ${res.data.new_invoice_number}. Old invoice voided in Zoho.`);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to regenerate invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="vo-regen-dialog">
        <DialogHeader>
          <DialogTitle>Fix Invoice — Regenerate in Company Name</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-2.5 rounded bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-xs text-blue-900 dark:text-blue-200">
            This will <strong>void the existing Zoho invoice</strong> and issue a fresh one under the corrected company name + GSTIN. If the subscription is already paid, the new invoice will also be marked as Paid.
          </div>
          <div>
            <Label>Company Name *</Label>
            <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Fineaz Capital" data-testid="vo-regen-company" />
          </div>
          <div>
            <Label>GSTIN <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="29ABWPN1045P2ZR" maxLength={15} data-testid="vo-regen-gstin" className="font-mono" />
          </div>
          <div>
            <Label>Company Address <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Textarea value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} rows={2} data-testid="vo-regen-address" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="vo-regen-submit">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Regenerate Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
